import React, { useEffect, useState, useCallback } from "react";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { WanLabel } from "./WanNames";

// AQMPanel — bufferbloat control (CAKE qdisc) for the edge's WAN links.
// Mirrors StressTestPanel's polling/apply/clear pattern and reuses its
// `.stress-*` card/badge/event-log CSS classes for visual consistency.

function WANAqmCard({ wan, device, rate, onRateChange, onApply, onClear, applying, clearing }) {
  const applied = device?.applied;
  const hasIngress = !!device?.ifb;

  return (
    <div className={`stress-wan-card ${applied ? "impaired" : ""}`}>
      <div className="stress-wan-header">
        <div className="stress-wan-name">
          <span className={`stress-wan-dot ${applied ? "green" : "red"}`} />
          <WanLabel iface={wan} />
        </div>
        <div className="stress-wan-badge">
          {applied ? (
            <span className="pill-ok">CAKE ACTIVE</span>
          ) : (
            <span className="pill-err">DEFAULT QDISC</span>
          )}
        </div>
      </div>

      <div className="stress-active-summary">
        <span className="stress-tag">{device?.qdisc || (device?.present ? "unknown" : "interface not present")}</span>
        {hasIngress && <span className="stress-tag">ingress: {device.ifb_qdisc || device.ifb}</span>}
        {device?.capped && <span className="stress-tag">Held to {device.capped} both ways by its data budget</span>}
        {device?.error && <span className="stress-tag sm">{device.error}</span>}
      </div>

      <div className="stress-sliders">
        <div className="stress-slider-group">
          <div className="stress-slider-header">
            <label>Upload rate (CAKE bandwidth)</label>
          </div>
          <input
            type="text"
            placeholder="e.g. 900mbit, at least 1mbit — blank = fair-queue only, no bloat control"
            value={rate}
            onChange={(e) => onRateChange(e.target.value)}
            className="aqm-rate-input"
          />
        </div>
      </div>

      <div className="stress-wan-actions">
        <button className="primary-btn sm" onClick={onApply} disabled={applying}>
          {applying ? "Applying…" : "Apply CAKE"}
        </button>
        <button className="secondary-btn sm" onClick={onClear} disabled={clearing || !applied}>
          {clearing ? "Clearing…" : "Clear"}
        </button>
      </div>
    </div>
  );
}

function EventLog({ events }) {
  if (!events || events.length === 0) {
    return <div className="stress-events-empty">No AQM events yet.</div>;
  }
  return (
    <div className="stress-events-list">
      {events.slice().reverse().map((ev, i) => (
        <div key={i} className="stress-event-row">
          <span className={`stress-event-action ${ev.action}`}>{ev.action.toUpperCase()}</span>
          {ev.iface && <span className="stress-event-wan"><WanLabel iface={ev.iface} /></span>}
          <span className="stress-event-details">{ev.details}</span>
          <span className="stress-event-time">
            {ev.time ? new Date(ev.time).toLocaleTimeString() : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AQMPanel({ nodeApiUrl, selectedNode }) {
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [rates, setRates] = useState({});
  const [applyingWan, setApplyingWan] = useState("");
  const [applyingAll, setApplyingAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [error, setError] = useState("");
  // An apply or clear the appliance refused, kept until the next one: the
  // status poll's own error clears every few seconds.
  const [actionError, setActionError] = useState("");

  const fetchJson = useCallback(async (path, options) => {
    const url = nodeApiUrl(path);
    if (!url) throw new Error("No node selected");
    const res = await fetchWithTimeout(url, options);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let message = text;
      try { message = JSON.parse(text).error || text; } catch { /* plain text */ }
      throw new Error(message || `${res.status}`);
    }
    if (res.status === 204) return null;
    return res.json();
  }, [nodeApiUrl]);

  useEffect(() => {
    if (!selectedNode) return;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const [statusRes, evRes] = await Promise.all([
          fetchJson("/aqm/status"),
          fetchJson("/aqm/events"),
        ]);
        if (!controller.signal.aborted) {
          const devs = Array.isArray(statusRes?.devices) ? statusRes.devices : [];
          setDevices(devs);
          setEvents(evRes?.events || []);
          setError("");
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      }
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, fetchJson]);

  const setRateFor = useCallback((wan, val) => {
    setRates((prev) => ({ ...prev, [wan]: val }));
  }, []);

  const applyOne = useCallback(async (wan) => {
    setApplyingWan(wan);
    try {
      const rate = (rates[wan] || "").trim();
      const body = rate ? { rates: { [wan]: { up: rate } } } : {};
      await fetchJson("/aqm/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setActionError("");
    } catch (err) {
      setActionError(err.message);
    } finally {
      setApplyingWan("");
    }
  }, [fetchJson, rates]);

  const clearOne = useCallback(async () => {
    // The API only exposes a whole-edge clear (mirrors how apply is
    // idempotent across all configured WANs); scope this button to
    // "clear everything" for now.
    setClearingAll(true);
    try {
      await fetchJson("/aqm/clear", { method: "POST" });
      setActionError("");
    } catch (err) {
      setActionError(err.message);
    } finally {
      setClearingAll(false);
    }
  }, [fetchJson]);

  const applyAll = useCallback(async () => {
    setApplyingAll(true);
    try {
      const rateOverrides = {};
      for (const [wan, rate] of Object.entries(rates)) {
        if (rate && rate.trim()) rateOverrides[wan] = { up: rate.trim() };
      }
      const body = Object.keys(rateOverrides).length ? { rates: rateOverrides } : {};
      await fetchJson("/aqm/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setActionError("");
    } catch (err) {
      setActionError(err.message);
    } finally {
      setApplyingAll(false);
    }
  }, [fetchJson, rates]);

  const anyApplied = devices.some((d) => d.applied);

  if (!selectedNode) {
    return (
      <div className="panel stress-panel">
        <div className="panel-title">AQM / Bufferbloat Control</div>
        <div className="panel-sub">Select an edge node to begin.</div>
      </div>
    );
  }

  return (
    <div className="panel stress-panel">
      <div className="stress-panel-header">
        <div>
          <div className="panel-title">AQM / Bufferbloat Control</div>
          <div className="panel-sub">
            Installs CAKE queueing on each WAN's upload path so a link driven
            to its capacity doesn't build a deep queue — the latency spike
            that can otherwise starve this edge's own health probes and knock
            a healthy WAN out of a SPREAD bond mid-transfer. Set an upload
            rate per WAN for real bloat control (blank = fair-queue only).
          </div>
        </div>
        <div className="aqm-header-actions">
          <button className="primary-btn" onClick={applyAll} disabled={applyingAll}>
            {applyingAll ? "Applying…" : "Apply to All WANs"}
          </button>
          {anyApplied && (
            <button className="danger-btn" onClick={clearOne} disabled={clearingAll}>
              {clearingAll ? "Clearing…" : "Clear All"}
            </button>
          )}
        </div>
      </div>

      {actionError && <div className="panel-error" role="alert">{actionError}</div>}
      {error && <div className="panel-error">{error}</div>}

      <div className="stress-wan-grid">
        {devices.filter((d) => d.kind === "wan" || !d.kind).map((d) => (
          <WANAqmCard
            key={d.iface}
            wan={d.iface}
            device={d}
            rate={rates[d.iface] || ""}
            onRateChange={(v) => setRateFor(d.iface, v)}
            onApply={() => applyOne(d.iface)}
            onClear={clearOne}
            applying={applyingWan === d.iface}
            clearing={clearingAll}
          />
        ))}
      </div>

      {devices.some((d) => d.kind === "lan" || d.kind === "tun") && (
        <div className="stress-active-summary" style={{ marginTop: 16 }}>
          {devices.filter((d) => d.kind === "lan" || d.kind === "tun").map((d) => (
            <span key={d.iface} className="stress-tag sm clean">
              {d.kind}: {d.iface} — {d.qdisc || (d.present ? "unknown" : "not present")}
            </span>
          ))}
        </div>
      )}

      <div className="panel-title" style={{ marginTop: 24 }}>Event Log</div>
      <EventLog events={events} />
    </div>
  );
}
